import { useMutation } from '@tanstack/react-query'
import { useLiveQuery } from 'dexie-react-hooks'
import { strapiPost, strapiPut, strapiDelete } from '@/lib/strapi'
import { db } from '@/lib/db/database'
import {
  openingHourMatchesSchedule,
  type OpeningHour,
  type OpeningHourSchedule,
} from '@/lib/types/openingHour'
import { useAppStore } from '@/lib/store/useAppStore'
import { toast } from 'sonner'

function wrapList<T>(data: T[] | undefined) {
  return {
    data: data !== undefined ? { data, meta: { pagination: { total: data.length, page: 1, pageSize: data.length, pageCount: 1 } } } : undefined,
    isLoading: data === undefined,
  }
}

export function useOpeningHours(schedule?: OpeningHourSchedule) {
  const data = useLiveQuery(async () => {
    const hours = await db.openingHours.toArray()
    return schedule ? hours.filter((hour) => openingHourMatchesSchedule(hour, schedule)) : hours
  }, [schedule])
  return wrapList(data)
}

export function useCreateOpeningHour() {
  return useMutation({
    mutationFn: async (data: Partial<OpeningHour>) => {
      const restaurantId = useAppStore.getState().restaurantId
      const resp = await strapiPost<OpeningHour>('/api/opening-hours', {
        data: { ...data, restaurant: restaurantId },
      })
      const oh = (resp as any).data ?? resp
      if (oh?.documentId) await db.openingHours.put(oh)
      return resp
    },
    onSuccess: () => toast.success('Opening hour created'),
    onError: () => toast.error('Failed to create opening hour'),
  })
}

export function useUpdateOpeningHour() {
  return useMutation({
    mutationFn: async ({ documentId, ...data }: { documentId: string } & Partial<OpeningHour>) => {
      const resp = await strapiPut<OpeningHour>(`/api/opening-hours/${documentId}`, { data })
      const oh = (resp as any).data ?? resp
      if (oh?.documentId) await db.openingHours.put(oh)
      return resp
    },
    onSuccess: () => toast.success('Opening hour updated'),
    onError: () => toast.error('Failed to update opening hour'),
  })
}

export function useDeleteOpeningHour() {
  return useMutation({
    mutationFn: async (documentId: string) => {
      const result = await strapiDelete(`/api/opening-hours/${documentId}`)
      await db.openingHours.delete(documentId)
      return result
    },
    onSuccess: () => toast.success('Opening hour deleted'),
    onError: () => toast.error('Failed to delete opening hour'),
  })
}

export function useCopyOpeningHoursFromSchedule() {
  return useMutation({
    mutationFn: async ({
      hours,
      sourceSchedule,
      targetSchedule,
    }: {
      hours: OpeningHour[]
      sourceSchedule: OpeningHourSchedule
      targetSchedule: OpeningHourSchedule
    }) => {
      const restaurantId = useAppStore.getState().restaurantId
      const sourceHours = hours.filter((hour) => openingHourMatchesSchedule(hour, sourceSchedule))
      const targetHours = hours.filter((hour) => openingHourMatchesSchedule(hour, targetSchedule))

      for (const hour of targetHours) {
        await strapiDelete(`/api/opening-hours/${hour.documentId}`)
        await db.openingHours.delete(hour.documentId)
      }

      const createdHours: OpeningHour[] = []
      for (const [index, hour] of sourceHours.entries()) {
        const resp = await strapiPost<OpeningHour>('/api/opening-hours', {
          data: {
            restaurant: restaurantId,
            bookosId: `oh-copy-${targetSchedule}-${Date.now()}-${index}`,
            mode: targetSchedule,
            special: hour.special,
            name: hour.name,
            day: hour.day,
            open: hour.open,
            close: hour.close,
            openSec: hour.openSec,
            closeSec: hour.closeSec,
            endDate: hour.endDate ?? null,
            maxBookings: hour.maxBookings,
            maxGuests: hour.maxGuests,
            maxBookingsInterval: hour.maxBookingsInterval,
            maxGuestsInterval: hour.maxGuestsInterval,
            note: hour.note ?? '',
            onlyCountOnlineBookings: hour.onlyCountOnlineBookings,
            useDurationByPeople: hour.useDurationByPeople,
            durationByPeople: hour.durationByPeople ?? [],
            seatingActive: hour.seatingActive,
            seatingInterval: hour.seatingInterval,
            seatingDuration: hour.seatingDuration,
            paymentActive: hour.paymentActive,
            useWaitlist: hour.useWaitlist,
            areas: hour.areas?.map((area) => area.id) ?? [],
          },
        })
        const created = (resp as any).data ?? resp
        if (created?.documentId) {
          createdHours.push(created)
          await db.openingHours.put(created)
        }
      }

      return createdHours
    },
    onSuccess: () => toast.success('Opening hours copied'),
    onError: () => toast.error('Failed to copy opening hours'),
  })
}
